import { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Box, Chip, TextField } from '@mui/material';
import { sendServerRequest } from '../../server/request';
import { URLS } from '../../env/urls';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';

interface CltError {
  id: string;
  type: string;
  provider: string | null;
  bancalizadora: string | null;
  cpf: string | null;
  message: string;
  status: number | null;
  response: any;
  path: string | null;
  createdAt: Date;
}

export function CltConsultErrors({ chatId }: { chatId: number }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<CltError[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <>
      <Button fullWidth variant="text" onClick={load}>Ver erros de consulta CLT</Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Erros de Consulta CLT</DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularLoading />
          ) : errors.length === 0 ? (
            <p>Sem erros registrados.</p>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              {errors.map((error) => (
                <ErrorCard
                  key={error.id}
                  error={error}
                  expanded={expanded === error.id}
                  onToggle={() => setExpanded(expanded === error.id ? null : error.id)}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  function load() {
    setOpen(true);
    if (errors.length > 0) return;
    setLoading(true);
    sendServerRequest('GET', URLS.fgtsChat.cltConsultErrors(chatId))
      .then((e) => {
        setErrors(e.data?.data?.errors || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar os erros.', 'error');
      });
  }
}

function ErrorCard({ error, expanded, onToggle }: { error: CltError; expanded: boolean; onToggle: () => void }) {
  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        p: 2,
        cursor: 'pointer',
        '&:hover': { bgcolor: '#f5f5f5' },
        transition: 'all 0.2s',
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <Chip
          label={error.provider || 'desconhecido'}
          size="small"
          variant="outlined"
        />
        {error.status && (
          <Chip
            label={`Status ${error.status}`}
            size="small"
            color={error.status >= 500 ? 'error' : 'warning'}
            variant="outlined"
          />
        )}
        <span style={{ fontSize: '0.85em', color: '#666' }}>
          {new Date(error.createdAt).toLocaleString('pt-BR')}
        </span>
      </Box>

      <div style={{ fontSize: '0.95em', color: '#333', marginBottom: 8 }}>
        <strong>Mensagem:</strong> {error.message}
      </div>

      {expanded && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          {error.cpf && (
            <div style={{ marginBottom: 8, fontSize: '0.9em' }}>
              <strong>CPF:</strong> {error.cpf}
            </div>
          )}
          {error.bancalizadora && (
            <div style={{ marginBottom: 8, fontSize: '0.9em' }}>
              <strong>Bancalizadora:</strong> {error.bancalizadora}
            </div>
          )}
          {error.path && (
            <div style={{ marginBottom: 8, fontSize: '0.85em' }}>
              <strong>Endpoint:</strong>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={error.path}
                InputProps={{ readOnly: true }}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </div>
          )}
          {error.response && (
            <div style={{ marginBottom: 8, fontSize: '0.85em' }}>
              <strong>Response:</strong>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={typeof error.response === 'string' ? error.response : JSON.stringify(error.response, null, 2)}
                InputProps={{ readOnly: true }}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </div>
          )}
        </Box>
      )}

      {!expanded && (
        <div style={{ fontSize: '0.85em', color: '#999', marginTop: 8 }}>
          Clique para ver mais detalhes
        </div>
      )}
    </Box>
  );
}
